import { Head, router, useForm } from "@inertiajs/react"
import { Camera, Send, Trash2, Type, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { useAuth } from "@/hooks/use-auth"
import { useInitials } from "@/hooks/use-initials"
import AppLayout from "@/layouts/app-layout"
import { feedPath } from "@/routes"

interface StoryFormData {
  media_type: "image" | "video"
  media?: File
  caption?: string
  base?: string // For Rails validation errors
}

interface NewStoryProps {
  story: Partial<StoryFormData>
  user_stories?: {
    id: string
    media_url?: string
    created_at: string
    views_count: number
    time_remaining_hours: number
  }[]
}

export default function NewStory({ story, user_stories = [] }: NewStoryProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [deletingStoryId, setDeletingStoryId] = useState<string | null>(null)
  const [step, setStep] = useState<"upload" | "preview">("upload")
  const previewContainerRef = useRef<HTMLDivElement>(null)
  const [previewHeight, setPreviewHeight] = useState(0)
  const getInitials = useInitials()
  const { user } = useAuth()

  useEffect(() => {
    if (step !== "preview") return
    const calculate = () => {
      const container = previewContainerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      const isMobile = window.innerWidth < 768
      const bottomNav = isMobile ? 64 : 0
      const layoutPadding = isMobile ? 64 : 24 // pb-16 on mobile, py-6 on desktop
      const buttonsAndPadding = 48
      const available =
        window.innerHeight -
        rect.top -
        bottomNav -
        layoutPadding -
        buttonsAndPadding
      const maxDesktop = window.innerWidth >= 768 ? 600 : Infinity
      setPreviewHeight(Math.min(Math.max(available, 200), maxDesktop))
    }
    calculate()
    window.addEventListener("resize", calculate)
    return () => window.removeEventListener("resize", calculate)
  }, [step])

  const [processing, setProcessing] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const { data, setData } = useForm<StoryFormData>({
    media_type: story.media_type ?? "image",
    caption: "",
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setData("media", file)

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string)
      }
      reader.readAsDataURL(file)

      // Detect media type
      if (file.type.startsWith("video/")) {
        setData("media_type", "video")
      } else {
        setData("media_type", "image")
      }
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    setErrors({})

    // Crear FormData con el wrapper story[...] que Rails espera
    const formData = new FormData()
    formData.append("story[media_type]", data.media_type)
    if (data.media) {
      formData.append("story[media]", data.media)
    }
    if (data.caption?.trim()) {
      formData.append("story[caption]", data.caption.trim())
    }

    router.post("/historias", formData, {
      forceFormData: true,
      onSuccess: () => {
        router.visit(feedPath())
      },
      onError: (err) => {
        console.error("Error creating story:", err)
        setErrors(err as Record<string, string>)
      },
      onFinish: () => {
        setProcessing(false)
      },
    })
  }

  const handleDeleteStory = (storyId: string) => {
    if (!confirm("¿Estás seguro de que quieres eliminar esta historia?")) {
      return
    }

    setDeletingStoryId(storyId)
    router.delete(`/historias/${storyId}`, {
      onSuccess: () => {
        router.reload({ only: ["user_stories"] })
      },
      onFinish: () => {
        setDeletingStoryId(null)
      },
    })
  }

  const removePreview = () => {
    setPreviewUrl(null)
    setData("media", undefined)
    setStep("upload")
  }

  const goToPreview = () => {
    if (data.media && previewUrl) {
      setStep("preview")
    }
  }

  const goBackToUpload = () => {
    setStep("upload")
  }

  return (
    <AppLayout>
      <Head title="Crear Historia" />

      <div
        className={
          step === "preview"
            ? "mx-auto max-w-2xl px-4 pt-2 sm:px-6"
            : "mx-auto max-w-2xl p-4 sm:p-6"
        }
      >
        {step === "upload" && (
          <>
            <div className="mb-6">
              <h1 className="text-2xl font-bold">Crear Historia</h1>
              <p className="text-muted-foreground mt-1 text-sm">
                Comparte un momento con la comunidad (expira en 24 horas)
              </p>
            </div>

            <div className="space-y-4">
              {/* Preview de imagen seleccionada */}
              {previewUrl && (
                <div className="flex items-start justify-center gap-2">
                  {data.media_type === "video" ? (
                    <video
                      src={previewUrl}
                      className="aspect-[9/16] h-80 rounded-lg object-cover"
                      controls
                    />
                  ) : (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="aspect-[9/16] h-80 rounded-lg object-cover"
                    />
                  )}
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="mt-1 flex-shrink-0"
                    onClick={removePreview}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Media Upload */}
              {!previewUrl && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="bg-card h-32 flex-1"
                      onClick={() =>
                        document.getElementById("media-file")?.click()
                      }
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Camera className="h-8 w-8" />
                        <span>Subir Imagen o Video</span>
                      </div>
                    </Button>
                    <input
                      id="media-file"
                      type="file"
                      accept="image/*,video/*"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>
                  {errors.media && (
                    <p className="text-destructive text-sm">{errors.media}</p>
                  )}
                  {errors.base && (
                    <p className="text-destructive text-sm">{errors.base}</p>
                  )}
                </div>
              )}

              {/* Caption input */}
              {previewUrl && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-0.5">
                    <div className="flex items-center gap-2">
                      <Type className="text-muted-foreground h-4 w-4" />
                      <span className="text-sm font-medium">
                        Texto (opcional)
                      </span>
                    </div>
                    <p className="text-muted-foreground text-xs">
                      {(data.caption ?? "").length}/150
                    </p>
                  </div>
                  <Input
                    placeholder="Escribe algo sobre tu historia..."
                    value={data.caption ?? ""}
                    onChange={(e) => setData("caption", e.target.value)}
                    maxLength={150}
                    className="bg-card"
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => router.visit(feedPath())}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  className="flex-1"
                  disabled={!data.media}
                  onClick={goToPreview}
                >
                  Vista previa
                </Button>
              </div>
            </div>

            {/* Mis Historias Activas */}
            {user_stories.length > 0 && (
              <div className="mt-4">
                <CardTitle className="text-lg">Tus Historias Activas</CardTitle>
                <CardContent className="border-chart-4 border-b px-0">
                  <div className="space-y-3">
                    {user_stories.map((userStory) => (
                      <div
                        key={userStory.id}
                        className="flex items-center justify-between rounded-lg p-3"
                      >
                        <div className="flex flex-1 items-center gap-3">
                          {userStory.media_url && (
                            <img
                              src={userStory.media_url}
                              alt="Historia"
                              className="h-12 w-12 rounded object-cover"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-muted-foreground text-xs">
                              {userStory.views_count} vistas · Expira en{" "}
                              {userStory.time_remaining_hours}h
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteStory(userStory.id)}
                          disabled={deletingStoryId === userStory.id}
                        >
                          <Trash2 className="text-destructive h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </div>
            )}
          </>
        )}

        {step === "preview" && previewUrl && (
          <div
            ref={previewContainerRef}
            className="mx-auto"
            style={{
              maxWidth:
                previewHeight > 0 ? `${(previewHeight * 9) / 16}px` : "360px",
            }}
          >
            <div
              className="relative overflow-hidden rounded-xl bg-black"
              style={{
                height: previewHeight > 0 ? `${previewHeight}px` : "auto",
              }}
            >
              {data.media_type === "video" ? (
                <video
                  src={previewUrl}
                  className="h-full w-full object-cover"
                  controls
                />
              ) : (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="h-full w-full object-cover"
                />
              )}

              {/* Caption overlay en preview */}
              {data.caption?.trim() && (
                <div className="absolute right-0 bottom-4 left-0 px-4">
                  <div className="flex items-start gap-2">
                    <Avatar className="mt-0.5 h-6 w-6 flex-shrink-0">
                      <AvatarImage
                        src={user?.avatar_url ?? undefined}
                        alt={user?.name ?? ""}
                      />
                      <AvatarFallback className="text-primary gradient-avatar text-[10px] font-semibold">
                        {getInitials(user?.name ?? "")}
                      </AvatarFallback>
                    </Avatar>
                    <p className="text-sm leading-snug font-medium text-white drop-shadow-lg">
                      {data.caption.trim()}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="mt-2">
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={goBackToUpload}
                >
                  Editar
                </Button>
                <Button type="submit" className="flex-1" disabled={processing}>
                  <Send className="mr-2 h-4 w-4" />
                  {processing ? "Publicando..." : "Publicar"}
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
